import React, { useEffect, useState } from 'react'
import type { PublicProfileCollection } from 'apollo-gql'
import { useMarketContext } from 'contexts/MarketContext'
import prefetchProfileCollectionPage from 'graphql/prefetchers/prefetchProfileCollectionPage'
import { initializeApollo } from 'lib/apolloClient'
import { setCacheControlHeader } from 'lib/cache'
import { useTranslation } from 'lib/i18n'
import { isServer } from 'lib/utils'
import { NextPage } from 'next'
import { NextPageContext } from 'next/dist/shared/lib/utils'
import { NextSeo } from 'next-seo'

import CollectionPage from 'components/CollectionPage/CollectionPage'
import CollectionPageHeader from 'components/CollectionPageHeader/CollectionPageHeader'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Heading from 'components/Heading/Heading'
import MarkdownContent from 'components/MarkdownContent/MarkdownContent'
import ProfileCollectionNotFoundPage from 'components/NotFound/ProfileCollectionNotFoundPage'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import Wrapper from 'components/Wrapper/Wrapper'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useIsDefaultLanguage from 'hooks/useIsDefaultLanguage'
import usePublicProfileCollection from 'hooks/usePublicProfileCollection'
import useRouter from 'hooks/useRouter'

import { MEMMO_RED } from '@/constants'

type SeoProps = {
  profileCollection: PublicProfileCollection
  title: string
  description: string
}

function Seo({ profileCollection, title, description }: SeoProps) {
  const { t } = useTranslation()
  const { market } = useMarketContext()
  const { selectedMarket } = market
  const isDefaultLanguage = useIsDefaultLanguage()

  if (!profileCollection) return null

  const meta = {
    title: t(
      'profileCollection.slug.meta.title2',
      '{{collectionName}} - memmo - {{localMarketName}}',
      {
        collectionName: title,
        localMarketName: selectedMarket.localName,
      },
    ),
    description,
  }

  const openGraph = {
    ...meta,
  }

  return (
    <NextSeo
      title={meta.title}
      description={meta.description}
      openGraph={openGraph}
      noindex={!isDefaultLanguage}
      nofollow={!isDefaultLanguage}
    />
  )
}

const ProfileCollectionPageBySlug: NextPage = (): JSX.Element => {
  const { browsability } = useFeatureFlags()
  const router = useRouter()
  const collectionSlug =
    (typeof router.query?.collectionSlug === 'string' &&
      router.query.collectionSlug) ||
    ''
  const page =
    (typeof router.query?.page === 'string' && router.query.page) || ''

  const [userInteracted, setUserInteracted] = useState(false)

  const afterLoadMore = () => {
    setUserInteracted(true)
  }

  // Scroll to top whenever we change page in (pagination)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [router.query])

  const { profileCollection, loading, error } = usePublicProfileCollection(
    collectionSlug,
    {},
    {
      fetchPolicy: 'cache-first',
      returnPartialData: true,
    },
    { page, afterLoadMore },
  )
  const foundProfileCollection =
    !!profileCollection && Object.keys(profileCollection).length

  if (error) return <ErrorMessage message="Error loading collection." />

  if ((!loading || isServer()) && !foundProfileCollection)
    return <ProfileCollectionNotFoundPage />

  const accentColor = profileCollection?.publicCmsItem?.color || MEMMO_RED

  const {
    title: originalTitle,
    description,
    seoContent,
    metaTitle,
    altTitle,
    additionalText,
  } = profileCollection?.publicCmsItem || {}
  const title = altTitle || originalTitle
  // @todo Add language alternates in NextSeo
  return (
    <PublicLayout
      headerBackground={accentColor}
      headerBorder={Boolean(description)}
      headerContent={
        (description || additionalText || browsability) && (
          <CollectionPageHeader
            title={title}
            description={description}
            additionalText={additionalText}
            userInteracted={userInteracted}
          />
        )
      }
    >
      <Seo
        title={metaTitle || title}
        description={description}
        profileCollection={profileCollection}
      />
      {!description && !browsability && (
        <Wrapper style={{ paddingTop: 20 }}>
          <Heading level="h3" color="dark">
            <h1 data-cy="collection-title">{title}</h1>
          </Heading>
        </Wrapper>
      )}

      <CollectionPage
        profileCollection={profileCollection}
        loading={loading}
        collectionSlug={collectionSlug}
      />

      {!!seoContent && (
        <Wrapper style={{ paddingTop: 20 }}>
          <div data-cy="seo-content">
            <MarkdownContent source={seoContent} />
          </div>
        </Wrapper>
      )}
    </PublicLayout>
  )
}

ProfileCollectionPageBySlug.getInitialProps = async (ctx: NextPageContext) => {
  const { collectionSlug, page } = ctx.query

  if (isServer()) {
    setCacheControlHeader({ ctx })
    const apolloClient = initializeApollo(ctx)
    await prefetchProfileCollectionPage(apolloClient, { collectionSlug }, page)

    return {
      initialApolloState: apolloClient.cache.extract(),
    }
  }

  return {}
}

export default ProfileCollectionPageBySlug
